import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { availableWorkspaces, clearWorkspace, getWorkspace } from '../utils/workspace';
import ThemePicker from '../components/ThemePicker';
import DeleteAccountModal from '../components/DeleteAccountModal';
import LanAccessPanel from '../components/LanAccessPanel';
import OfflineAreasPanel from '../components/OfflineAreasPanel';
import NotificationSettings from '../components/pipeline/NotificationSettings';
import { focusElement } from '../utils/useFocusOnOpen';

/*
 * /account -- the signed-in person's own settings.
 *
 * "Delete my account" used to sit directly under "Log out", in both the
 * desktop account menu and the phone menu. The dialog asks for the password,
 * so a slip deleted nothing by itself, but an irreversible account action does
 * not belong one row from the most-used button in the menu. It lives here now,
 * at the bottom, behind its own heading and explanation.
 *
 * Everything shown in Profile comes from the session token's claims. The token
 * carries username, initials and roles; it does not carry the email address,
 * so that row appears only if a future token adds it.
 *
 * 'public' accounts never reach this page (PrivateRoute sends them to /survey,
 * which has its own delete button).
 */

/* Each language is named in its own script, so it can be found by someone who
 * cannot read the language the page is currently in. */
const LANGUAGE_CHOICES = [
  { code: 'en', name: 'English' },
  { code: 'he', name: 'עברית' },
];

const Svg = ({ children }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {children}
  </svg>
);
const ProfileIcon = () => <Svg><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></Svg>;
const SlidersIcon = () => (
  <Svg>
    <line x1="4" y1="6" x2="20" y2="6" /><circle cx="9" cy="6" r="2" />
    <line x1="4" y1="12" x2="20" y2="12" /><circle cx="15" cy="12" r="2" />
    <line x1="4" y1="18" x2="20" y2="18" /><circle cx="8" cy="18" r="2" />
  </Svg>
);
const WifiIcon = () => (
  <Svg>
    <path d="M2 8.5a15 15 0 0 1 20 0" /><path d="M5 12a10 10 0 0 1 14 0" /><path d="M8.5 15.5a5 5 0 0 1 7 0" />
    <line x1="12" y1="19" x2="12" y2="19" />
  </Svg>
);
const LockIcon = () => <Svg><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></Svg>;
const WarningIcon = () => (
  <Svg><path d="M12 3 2 21h20L12 3z" /><line x1="12" y1="10" x2="12" y2="14" /><line x1="12" y1="17.5" x2="12" y2="17.5" /></Svg>
);

function Section({ id, title, icon, danger = false, children }) {
  return (
    <section aria-labelledby={id} style={danger ? styles.dangerCard : styles.card}>
      <h2 id={id} style={danger ? styles.dangerHeading : styles.heading}>
        {icon}
        <span>{title}</span>
      </h2>
      {children}
    </section>
  );
}

/* A label beside its value on a wide screen, above it on a narrow one. */
function Row({ label, labelId, children, hint }) {
  return (
    <div style={styles.row}>
      <div id={labelId} style={styles.rowLabel}>{label}</div>
      <div style={styles.rowValue}>
        {children}
        {hint && <p style={styles.hint}>{hint}</p>}
      </div>
    </div>
  );
}

export default function AccountSettings() {
  const { t, i18n } = useTranslation('account');
  const { t: tUsers } = useTranslation('users');
  const { t: tWorkspace } = useTranslation('workspace');
  const { t: tPipeline } = useTranslation('pipeline');
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteButtonRef = useRef(null);
  const languageRefs = useRef({});

  const chooseLanguage = (code) => {
    if (code !== i18n.language) i18n.changeLanguage(code);
  };

  // A radio group moves with the arrow keys and is one tab stop. Left and
  // right swap meaning in a right-to-left page, as they do on screen.
  const onLanguageKey = (event, index) => {
    const forward = ['ArrowDown', document.documentElement.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight'];
    const back = ['ArrowUp', document.documentElement.dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft'];
    const step = forward.includes(event.key) ? 1 : back.includes(event.key) ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const n = LANGUAGE_CHOICES.length;
    const { code } = LANGUAGE_CHOICES[(index + step + n) % n];
    chooseLanguage(code);
    languageRefs.current[code]?.focus();
  };

  // ChangePassword sends a voluntary change back here with this flag. It is
  // read once and then cleared from history, so a reload does not announce
  // the change a second time.
  const [passwordChanged] = useState(() => Boolean(location.state?.passwordChanged));
  // The button that was pressed went away with the page it was on, so focus
  // is moved to the confirmation: a status region that arrives already
  // filled is often not announced, but a focused element is always read.
  const changedRef = useRef(null);
  useEffect(() => {
    if (location.state?.passwordChanged) {
      navigate(location.pathname, { replace: true, state: null });
    }
    if (passwordChanged) focusElement(changedRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const workspaces = availableWorkspaces(user);
  const currentWorkspace = getWorkspace(user);

  const changeWorkspace = () => {
    // Cleared first, or the chooser would bounce straight back to the stored
    // workspace instead of asking (same as the account menu does it).
    clearWorkspace();
    navigate('/workspace');
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    // Focus goes back to the control that opened the dialog, not to the top
    // of the document.
    deleteButtonRef.current?.focus();
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>{t('settings.title')}</h1>
        <p style={styles.subtitle}>{t('settings.subtitle')}</p>
      </header>

      {passwordChanged && (
        <div ref={changedRef} tabIndex={-1} role="status" data-testid="password-changed" style={styles.success}>
          {t('settings.passwordChanged')}
        </div>
      )}

      <Section id="account-profile" title={t('settings.profile.title')} icon={<ProfileIcon />}>
        <Row label={t('settings.profile.username')}>
          <span style={styles.value} dir="auto">{user?.username || '—'}</span>
        </Row>
        {user?.email && (
          <Row label={t('settings.profile.email')}>
            <span style={styles.value} dir="ltr">{user.email}</span>
          </Row>
        )}
        {user?.initials && (
          <Row label={t('settings.profile.initials')}>
            <span style={styles.value} dir="auto">{user.initials}</span>
          </Row>
        )}
        <Row label={t('settings.profile.roles')} hint={t('settings.profile.rolesNote')}>
          {roles.length === 0 ? (
            <span style={styles.muted}>{t('settings.profile.noRoles')}</span>
          ) : (
            <ul style={styles.chips} aria-label={t('settings.profile.roles')}>
              {roles.map(role => (
                <li key={role} style={styles.chip}>
                  {tUsers(`role.${role}`, { defaultValue: role.replace(/_/g, ' ') })}
                </li>
              ))}
            </ul>
          )}
        </Row>
      </Section>

      <Section id="account-preferences" title={t('settings.preferences.title')} icon={<SlidersIcon />}>
        <Row label={t('settings.preferences.language')} labelId="account-language-label"
             hint={t('settings.preferences.languageHint')}>
          <div role="radiogroup" aria-labelledby="account-language-label" style={styles.segmented}>
            {LANGUAGE_CHOICES.map(({ code, name }, index) => {
              const on = i18n.language === code;
              return (
                <button
                  key={code}
                  ref={el => { languageRefs.current[code] = el; }}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  tabIndex={on || !LANGUAGE_CHOICES.some(c => c.code === i18n.language) ? 0 : -1}
                  lang={code}
                  title={t('tip.settingsLanguage')}
                  onClick={() => chooseLanguage(code)}
                  onKeyDown={e => onLanguageKey(e, index)}
                  style={on ? styles.segmentOn : styles.segment}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </Row>

        <Row label={t('settings.preferences.theme')} labelId="account-theme-label"
             hint={t('settings.preferences.themeHint')}>
          <ThemePicker variant="page" labelledBy="account-theme-label" />
        </Row>

        {workspaces.length > 0 && (
          <Row
            label={t('settings.preferences.workspace')}
            hint={workspaces.length === 1 ? t('settings.preferences.workspaceSingle') : null}
          >
            <div style={styles.inline}>
              <span style={styles.value}>
                {currentWorkspace
                  ? tWorkspace('switcher.current', { name: tWorkspace(`${currentWorkspace}.name`) })
                  : t('settings.preferences.workspaceNone')}
              </span>
              {workspaces.length > 1 && (
                <button type="button" onClick={changeWorkspace} title={t('tip.settingsChangeWorkspace')}
                        style={styles.secondaryButton}>
                  {tWorkspace('switcher.change')}
                </button>
              )}
            </div>
          </Row>
        )}
      </Section>

      {/* The desktop app only: whether phones on this Wi-Fi may reach this
          computer (they upload their photos to it), and the address they open.
          Nothing at all on the cloud app, whose API has no such route. */}
      <LanAccessPanel wrap={(children) => (
        <Section id="account-network" title={t('settings.lan.title')} icon={<WifiIcon />}>{children}</Section>
      )} />

      {/* My email opt-outs: the culture pipeline's and the specimen handoffs', each by role. */}
      <NotificationSettings user={user} wrap={(children) => (
        <Section id="account-notifications" title={tPipeline('notifications.title')} icon={<SlidersIcon />}>
          {children}
        </Section>
      )} />

      {/* The map and road-and-trail data kept on this device for offline use. */}
      <OfflineAreasPanel />

      <Section id="account-security" title={t('settings.security.title')} icon={<LockIcon />}>
        <Row label={t('settings.security.password')} hint={t('settings.security.passwordHint')}>
          <Link to="/change-password" title={t('tip.settingsChangePassword')} style={styles.secondaryButton}>
            {t('settings.security.changePassword')}
          </Link>
        </Row>
      </Section>

      {/* Kept apart from everything above: its own margin, border and colour,
          and the explanation comes before the button rather than after it. */}
      <Section id="account-danger" title={t('settings.danger.title')} icon={<WarningIcon />} danger>
        <p style={styles.dangerIntro}>{t('settings.danger.intro')}</p>
        <div style={styles.dangerRow}>
          <div style={{ flex: '1 1 280px', minWidth: 0 }}>
            <h3 style={styles.dangerItemTitle}>{t('settings.danger.deleteTitle')}</h3>
            <p style={styles.body}>{t('settings.danger.deleteBody')}</p>
            <p style={styles.hint}>{t('settings.danger.deleteNote')}</p>
          </div>
          <button
            ref={deleteButtonRef}
            type="button"
            onClick={() => setDeleteOpen(true)}
            title={t('tip.settingsDelete')}
            aria-haspopup="dialog"
            style={styles.dangerButton}
          >
            {t('deleteAccount')}
          </button>
        </div>
      </Section>

      {deleteOpen && <DeleteAccountModal onClose={closeDelete} />}
    </div>
  );
}

const card = {
  background: 'var(--c-surface)',
  border: '1px solid var(--c-border)',
  borderRadius: 12,
  padding: '18px 20px',
  marginBottom: 16,
};

const button = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 44,
  padding: '8px 16px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const styles = {
  page: { maxWidth: 760, margin: '0 auto', padding: '8px 0 32px', color: 'var(--c-text-fg)' },
  header: { marginBottom: 20 },
  title: { margin: '0 0 4px', fontSize: 24 },
  subtitle: { margin: 0, fontSize: 14, color: 'var(--c-text-muted-fg)' },
  success: {
    background: 'var(--c-primary-soft)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary-border)',
    borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 16,
  },
  card,
  heading: {
    display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 12px', fontSize: 17,
    color: 'var(--c-text-fg)',
  },
  row: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '6px 16px',
    padding: '12px 0', borderTop: '1px solid var(--c-border)',
  },
  rowLabel: { flex: '0 0 160px', fontSize: 14, fontWeight: 600, color: 'var(--c-text-body-fg)', paddingTop: 2 },
  rowValue: { flex: '1 1 260px', minWidth: 0, textAlign: 'start' },
  value: { fontSize: 15, overflowWrap: 'anywhere' },
  muted: { fontSize: 14, color: 'var(--c-text-muted-fg)' },
  hint: { margin: '6px 0 0', fontSize: 13, color: 'var(--c-text-muted-fg)', lineHeight: 1.5 },
  body: { margin: '0 0 4px', fontSize: 14, color: 'var(--c-text-body-fg)', lineHeight: 1.6 },
  inline: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6, listStyle: 'none', margin: 0, padding: 0 },
  chip: {
    fontSize: 13, padding: '3px 10px', borderRadius: 999, background: 'var(--c-primary-soft)',
    color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary-border)',
  },
  segmented: {
    display: 'inline-flex', border: '1px solid var(--c-border-strong)', borderRadius: 8, overflow: 'hidden',
  },
  segment: {
    ...button, borderRadius: 0, border: 'none', fontWeight: 500, minWidth: 96,
    background: 'var(--c-surface)', color: 'var(--c-text-fg)',
  },
  segmentOn: {
    ...button, borderRadius: 0, border: 'none', minWidth: 96,
    background: 'var(--c-primary)', color: 'var(--c-on-accent)',
  },
  secondaryButton: {
    ...button, background: 'var(--c-surface)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)',
  },
  dangerCard: {
    ...card,
    marginTop: 40,
    border: '1px solid var(--c-danger-border)',
    background: 'var(--c-danger-soft-4)',
  },
  dangerHeading: {
    display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 4px', fontSize: 17, color: 'var(--c-danger-fg)',
  },
  dangerIntro: { margin: '0 0 12px', fontSize: 13, color: 'var(--c-danger-fg)' },
  dangerRow: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16,
    paddingTop: 12, borderTop: '1px solid var(--c-danger-border)',
  },
  dangerItemTitle: { margin: '0 0 4px', fontSize: 15, color: 'var(--c-text-fg)' },
  dangerButton: {
    ...button, background: 'var(--c-danger)', color: 'var(--c-on-accent)', border: '1px solid var(--c-danger)',
  },
};
